import type { AppConfig } from '@jwp/ott-common/types/config';

/**
 * Former Firebase Remote Config key: `web_config`.
 *
 * NOTE: The following fields are NOT present in the Firebase `web_config` JSON payload.
 * They were added/kept here only because `AppConfig` (config.ts) declares them as required.
 * Update these to real values when available:
 * - feature.isUtmEnabled
 * - system73.system73AccessKey
 * - video_details.epg.is_catchup_hours_enabled
 * - footer.*
 * - ui_controls.single_click
 * - ui_controls.cookies.type
 * - ui_controls.cookies.require_explicit_consent
 *
 * Also note: source JSON had `end_point.user.change_password: true` (boolean),
 * but `ApiConfig` requires it to be a string — set to '' below. Fix if a real
 * endpoint path exists.
 *
 * Environment variables used here:
 * - APP_ANDROID_PLAY_STORE_URL / APP_IOS_APP_STORE_URL — footer store links (optional)
 */

const baseUrl = (import.meta.env.APP_BASE_URL || 'https://dev-play.katon360.com/').replace(/\/?$/, '/');

export const webConfig: AppConfig = {
  api: {
    base_url: `${baseUrl}api/v1`,
    end_point: {
      user: {
        get_user_profile: '/profile',
        create_user_profile: '/profile',
        delete_account: '',
        change_password: '', // was `true` (boolean) in source JSON — type requires string
        activate_tv: '',
      },
      favorite: {
        get_favorite: '',
        update_favorite: '',
        remove_favorite: '',
      },
      watch_history: {
        get_watch_history: '',
        update_watch_history: '',
        remove_watch_history: '',
      },
    },
  },
  feature: {
    // NOT present in Firebase JSON
    isUtmEnabled: false,
  },
  lambda: {
    recaptcha_verification: 'https://mnwso5fsz5kugvmttlq4pdag5a0quqwt.lambda-url.us-east-1.on.aws/',
  },
  system73: {
    // NOT present in Firebase JSON
    system73AccessKey: '' as '',
  },
  branding: {
    logo_url: `${baseUrl}images/KatonPlay/Logo.svg`,
    primary_font: '"Poppins", sans-serif',
    secondary_font: '"Poppins", sans-serif',
    favicon_url: `${baseUrl}images/KatonPlay/FavIcon.svg`,
    client_site: 'katon360.com',
    support_email: 'https://www.katon360.com/contact-us',
    video_horizontal_thumbnail: `${baseUrl}images/KatonPlay/katonplay-horizontal-thumbnail.svg`,
    video_vertical_thumbnail: `${baseUrl}images/KatonPlay/katonplay-vertical-thumbnail.svg`,
    domain: baseUrl.replace(/\/$/, ''),
    footer_logo_url: `${baseUrl}images/KatonPlay/FooterLogo.svg`,
  },
  user_profile: {
    personal_details: {
      first_name_last_name: {
        enabled: false,
        required: false,
      },
      full_name: {
        enabled: true,
        required: true,
      },
      company_name: {
        enabled: false,
        required: false,
      },
      address: {
        enabled: false,
        required: false,
      },
      phone_number: {
        enabled: false,
        required: false,
      },
      birth_date: {
        enabled: false,
        required: false,
      },
    },
  },
  video_details: {
    epg: {
      // NOT present in Firebase JSON
      is_catchup_hours_enabled: false,
    },
  },
  ui_controls: {
    button_display_modes: {
      label_only: false,
      icon_only: true,
      label_with_icon: false,
    },
    // NOT present in Firebase JSON
    single_click: true,
    can_show_consent_management: false,
    cookies: {
      // NOT present in Firebase JSON
      type: 'essential_only',
      description: {
        en: 'We use only essential cookies necessary for the proper functioning of this site. By continuing to use this site, you agree to their use.',
        es: 'Solo utilizamos cookies esenciales necesarias para el funcionamiento adecuado de este sitio. Al continuar usando este sitio, aceptas su uso.',
      },
      button_label: {
        en: 'I Accept Cookies',
        es: 'Sí, acepto las cookies',
      },
      // NOT present in Firebase JSON
      require_explicit_consent: true,
      consent_cookie_name: 'ott-cookies',
      consent_cookie_expiry_days: 365,
    },
    subscription: {
      payment_success_description: {
        en: 'Your payment was completed successfully. Welcome to your subscription!',
        es: 'Tu pago se ha completado con éxito. ¡Bienvenido a tu suscripción!',
      },
      payment_cancelled_description: {
        en: 'You have cancelled the payment',
        es: 'Has cancelado el pago',
      },
      payment_failed_description: {
        en: 'Payment failed, please try again',
        es: 'El pago ha fallado, por favor inténtalo de nuevo',
      },
    },
  },
  footer: {
    // NOT present in Firebase JSON — kept previous defaults
    mobile_apps_links: {
      title: {
        en: '',
        es: '',
      },
      android: {
        enabled: false,
        icon_url: '',
        app_url: import.meta.env.APP_ANDROID_PLAY_STORE_URL || '',
      },
      ios: {
        enabled: false,
        icon_url: '',
        app_url: import.meta.env.APP_IOS_APP_STORE_URL || '',
      },
    },
  },
};
